import { ref, computed } from "vue";
import { add, isAfter, fromUnixTime, getUnixTime } from "date-fns";
import { defineStore } from "pinia";
import { useStorage } from "@vueuse/core";
import { useCookie } from "@/composables/authentication/useCookie";
import { useConsumerApi } from "@/composables/useConsumerApi";
import type {
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    ForgotPasswordRequest,
    CurrentUserResponse,
    PasswordChangeWithTokenRequest,
    AccountVerificationResponse,
    ResendVerificationRequest,
    ResendVerificationResponse,
    ProfileUpdateRequest,
    AvatarUploadResponse,
} from "@/types/api";

const hostname = window.location.hostname;
const isLocalDev = hostname.startsWith("local.");
const isShareableDomain = hostname.includes("corridorsafety.com");

// Detect environment from hostname (dev/qa/pt/prod)
const getEnvironmentTag = (): "dev" | "qa" | "pt" | "prod" => {
    if (hostname.includes(".dev.")) return "dev";
    if (hostname.includes(".qa.")) return "qa";
    if (hostname.includes(".pt.")) return "pt";
    return "prod";
};

// Environment-scoped cookie keys prevent cross-env token conflicts
const ENV_TAG = getEnvironmentTag();
const cookieKey = (base: string) => `${base}__${ENV_TAG}`;

// Use .corridorsafety.com domain to share cookies between:
// app.{env}.corridorsafety.com <-> app.{env}.{community}.corridorsafety.com
const getBaseDomain = () => {
    if (!isShareableDomain) return undefined;
    return ".corridorsafety.com";
};

const cookieOptions = {
    domain: getBaseDomain(),
    path: "/",
    secure: !isLocalDev,
    sameSite: "lax" as const,
};

// Store auth tokens in cookies for cross-subdomain sharing (except local dev)
// Local dev uses localStorage since HTTP cookies have cross-domain restrictions
const accessToken =
    isShareableDomain && !isLocalDev
        ? useCookie(cookieKey("accessToken"), null, cookieOptions)
        : useStorage<string | null>(cookieKey("accessToken"), null);
const accessTokenExpiresAt =
    isShareableDomain && !isLocalDev
        ? useCookie(cookieKey("accessTokenExpiresAt"), null, cookieOptions)
        : useStorage<number | null>(cookieKey("accessTokenExpiresAt"), null);

// User data in memory only (not in cookies to keep MQTT headers small)
const user = ref<CurrentUserResponse | null>(null);
const hasFetchedCurrentUser = ref(false);

export const useAuthenticationStore = defineStore("authentication", () => {
    const consumerApi = useConsumerApi();

    const $reset = () => {
        accessToken.value = null;
        accessTokenExpiresAt.value = null;
        user.value = null;
        hasFetchedCurrentUser.value = false;
    };

    const isAccessTokenExpired = (): boolean =>
        !accessTokenExpiresAt.value ||
        isAfter(Date.now(), fromUnixTime(accessTokenExpiresAt.value));

    const isAuthenticated = (): boolean => {
        return !!accessToken.value && !isAccessTokenExpired();
    };

    const fetchCurrentUser = async (): Promise<CurrentUserResponse> => {
        const response =
            await consumerApi.get<CurrentUserResponse>("/users/me");

        user.value = response.data;
        hasFetchedCurrentUser.value = true;

        // ⚠️ DEVELOPMENT ONLY: Mock Admin role for testing. Disabled — it granted a
        // fake Admin membership to any dev user without one, masking the web-app role
        // gate (a Member/FirstResponder-only account could sign in).
        // if (import.meta.env.DEV && user.value) {
        //     console.warn("🧪 DEV MODE: Mocking Admin role for testing");
        //
        //     // Add a mock Admin membership if user has no memberships or only Member role
        //     const hasAdminAccess = user.value.memberships?.some(
        //         (m) =>
        //             m.communityRole === "Admin" || m.communityRole === "Owner",
        //     );
        //
        //     if (!hasAdminAccess) {
        //         const mockCommunity = {
        //             id: 999,
        //             displayName: "Test Community",
        //             orgName: "test-community",
        //             description: "Mock community for testing",
        //             ownerId: user.value.id,
        //             isPublic: false,
        //             orgUrl: "corridorsafety.com",
        //             orgApiVersion: "v2",
        //             domainName: "test-community.corridorsafety.com",
        //             autoInvite: false,
        //         };
        //
        //         const mockMembership = {
        //             id: 999,
        //             userId: user.value.id,
        //             communityId: 999,
        //             communityRole: "Admin" as const,
        //             joinedAt: new Date().toISOString(),
        //             community: mockCommunity,
        //             user: {
        //                 id: user.value.id,
        //                 email: user.value.email,
        //                 displayName: user.value.displayName,
        //                 active: user.value.active,
        //             },
        //         };
        //
        //         user.value.memberships = [
        //             ...(user.value.memberships || []),
        //             mockMembership,
        //         ];
        //     }
        // }

        return response.data;
    };

    // Profile, phone number and emergency contacts are all saved in this one call.
    const updateProfile = async (
        profileRequest: ProfileUpdateRequest,
    ): Promise<CurrentUserResponse> => {
        const response = await consumerApi.post<CurrentUserResponse>(
            `/users/${profileRequest.userId}/profile`,
            profileRequest,
        );

        user.value = response.data;
        return response.data;
    };

    const uploadAvatar = async (file: Blob, fileName: string) => {
        const formData = new FormData();
        formData.append("file", file, fileName);

        const response = await consumerApi.post<AvatarUploadResponse>(
            "/profiles/avatar",
            formData,
            // Overrides the client's JSON default, which would serialize the FormData.
            { headers: { "Content-Type": "multipart/form-data" } },
        );

        return response.data;
    };

    const signIn = async (loginRequest: LoginRequest) => {
        $reset();

        const response = await consumerApi.post<LoginResponse>(
            "/authentication/login",
            loginRequest,
            {
                headers: {
                    "x-api-key": import.meta.env.VITE_X_API_KEY,
                },
            },
        );

        const { token, id, key, email, passwordExpiry } = response.data;

        // Extract expiration from JWT payload
        const decodeJWTPayload = (token: string) => {
            try {
                const payload = token.split(".")[1];
                const decoded = JSON.parse(atob(payload));
                return decoded;
            } catch (error) {
                console.error("Failed to decode JWT payload:", error);
                return null;
            }
        };

        const jwtPayload = decodeJWTPayload(token);
        const tokenExpiresAt = jwtPayload?.exp;

        accessToken.value = token;
        accessTokenExpiresAt.value = tokenExpiresAt;

        // Set initial user data from login response
        user.value = {
            id,
            email,
            key,
            displayName: email,
            active: true,
            passwordExpiry,
            role: "EndUser",
            profile: {
                id: 0,
                isFirstAidCertified: false,
                emergencyContacts: [],
            },
            memberships: [],
            devices: [],
        };

        // Fetch complete user profile with permissions
        try {
            await fetchCurrentUser();
        } catch (error) {
            console.warn("Failed to fetch user profile data:", error);
        }

        return response.data;
    };

    const signOut = async () => {
        try {
            if (accessToken.value) {
                await consumerApi.post(
                    "/authentication/logout",
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken.value}`,
                        },
                    },
                );
            }
        } catch (error) {
            console.error("Server logout failed", error);
        } finally {
            $reset();
        }
    };

    const register = async (registerRequest: RegisterRequest) => {
        return await consumerApi.post(
            "/authentication/register",
            registerRequest,
            {
                headers: {
                    "x-api-key": import.meta.env.VITE_X_API_KEY,
                },
            },
        );
    };

    const forgotPassword = async (
        forgotPasswordRequest: ForgotPasswordRequest,
    ) => {
        return await consumerApi.post(
            "/authentication/forgot-password",
            forgotPasswordRequest,
            {
                headers: {
                    "x-api-key": import.meta.env.VITE_X_API_KEY,
                },
            },
        );
    };

    const validateResetToken = async (token: string) => {
        return await consumerApi.get(
            `/authentication/reset-password/validate/${token}`,
            {
                headers: {
                    "x-api-key": import.meta.env.VITE_X_API_KEY,
                },
            },
        );
    };

    const accountVerification = async (
        token: string,
    ): Promise<AccountVerificationResponse> => {
        const response = await consumerApi.post<AccountVerificationResponse>(
            `/authentication/account-verification/${token}`,
            {},
            {
                headers: {
                    "x-api-key": import.meta.env.VITE_X_API_KEY,
                },
            },
        );

        return response.data;
    };

    // Sends a fresh registration token to an account that was registered but never
    // verified, for when the emailed link expired before it was clicked. Unauthenticated
    // by necessity: an unverified account can't obtain a bearer token.
    const resendVerification = async (
        resendVerificationRequest: ResendVerificationRequest,
    ): Promise<ResendVerificationResponse> => {
        const response = await consumerApi.post<ResendVerificationResponse>(
            "/authentication/resend-verification",
            resendVerificationRequest,
            {
                headers: {
                    "x-api-key": import.meta.env.VITE_X_API_KEY,
                },
            },
        );

        return response.data;
    };

    const changePasswordWithToken = async (
        token: string,
        passwordRequest: PasswordChangeWithTokenRequest,
    ) => {
        return await consumerApi.post(
            `/authentication/change-password/${token}`,
            passwordRequest,
            {
                headers: {
                    "x-api-key": import.meta.env.VITE_X_API_KEY,
                },
            },
        );
    };

    return {
        accessToken,
        accessTokenExpiresAt,
        isAccessTokenExpired,

        user,
        hasFetchedCurrentUser,
        isAuthenticated,
        $reset,
        signIn,
        signOut,
        fetchCurrentUser,
        updateProfile,
        uploadAvatar,
        register,
        forgotPassword,
        validateResetToken,

        accountVerification,
        resendVerification,
        changePasswordWithToken,
    };
});
